import { ReactNode, useMemo } from "react";
import { createFastContext } from "../../../contexts/createFastContext";
import { cloneObj } from "../../../util/varibles/global";
import { IChecklist } from "../../../util/varibles/interface";
import { IPropsElement } from "./constants";

const updateIndex = (form: (IPropsElement[])[]) => {
    let count = 0;
    return form.map(item => item.map(sub => {
        const value = { ...sub, index: count };
        count++;
        return value
    }))
}

export const contentToRow = (result: IPropsElement[]): IPropsElement[][] => {
    let row = -1, sub = 0;
    return result.reduce((rs: any[], item, index: number) => {
        const { subIndex = 0 } = item;
        if (sub >= subIndex)
            row++;
        sub = subIndex;
        const currentRow = rs[row] || [];
        rs[row] = [...currentRow, { ...item, index, subIndex: currentRow.length }];

        return rs;
    }, []);
}

interface IState {
    data: IChecklist | Partial<IChecklist>
    form: (IPropsElement[])[]
    palettePreview?: { rowIndex: number, position: number }
    dragging?: number
    selected?: number
    focusElement?: number
    rowSelected?: number
    getParentEl: () => HTMLDivElement | null
}

const {
    Provider,
    useStore: useChecklistEdit,
    setStore: setChecklistEdit,
    getStore: getChecklistEdit,
    callEvent: callEventChecklistEdit
} = createFastContext<IState, {
    onElementAdd(value: IPropsElement, index?: string, subIndex?: string): void
    onElementUpdate(value: IPropsElement): void
    onElementDelete(index: number): void
    onElementDuplicate(value: IPropsElement): void
    onElementChangePosition(value: IPropsElement, newIndex: number): void
    onElementChangeRow(value: IPropsElement, newRowIndex: number): void
    onRowUpdate(index: number, targetIndex: number): void
    onRowDelete(index: number): void
}>({
    data: {},
    form: [],
    getParentEl: () => null
});

const ChecklistEditProvider = ({ children, ...args }: { children: ReactNode } & Partial<IState>) => {
    const { data } = args;
    const form = useMemo(() => {
        if (data?.content) {
            try {
                const result = JSON.parse(data?.content);
                return contentToRow(result)
            } catch (error) {
                console.log("Error JSON: ", error);
                return [];
            }
        }
        return [];
    }, [data?.content]);

    return <Provider
        props={{ ...args, form }}
        event={{
            onElementAdd: (value, index, subIndex, { set }) => {
                set(({ form: prev }) => {
                    const newIndex = index ? +index : prev.length;
                    if (!prev[newIndex]) {
                        const { index: count = -1 } = prev[prev.length - 1]?.[prev[prev.length - 1].length - 1] || {}
                        return {
                            dragging: undefined,
                            form: [...prev, [{
                                ...value,
                                subIndex: 0,
                                index: count + 1,
                            } as IPropsElement]],
                            selected: count + 1,
                            focusElement: count + 1,
                            rowSelected: newIndex
                        }
                    }
                    let row = cloneObj(prev[newIndex] || []), count = row[0]?.index || 0, selected = 0;
                    if (subIndex) {
                        const newSubIndex = +subIndex;
                        row = row.forEach((item: IPropsElement, i: number) => {
                            if (i === newSubIndex) {
                                selected = count;
                                row.push({ ...item, index: count, subIndex: row.length })
                                count++;
                            }
                            row.push({ ...value, index: count, subIndex: row.length })
                            count++;
                        })
                    } else {
                        selected = count + row.length;
                        row.push({ ...value, index: selected, subIndex: row.length })
                    }
                    count = 0;
                    return {
                        dragging: undefined,
                        form: prev.reduce((rs: (IPropsElement[])[], item, i) => {
                            if (i === newIndex) {
                                count += row.length;
                                return [...rs, row];
                            }
                            return [...rs, item.map(sub => {
                                const value = { ...sub, index: count };
                                count++;
                                return value;
                            })];
                        }, []),
                        selected,
                        focusElement: selected,
                        rowSelected: newIndex
                    }
                })
            },
            onElementUpdate: (value: IPropsElement, { set }) => {
                set(({ form: prev, rowSelected }) => {
                    if (rowSelected === undefined)
                        return {}
                    const form: IPropsElement[][] = cloneObj(prev);
                    const { subIndex = 0 } = value;
                    form[rowSelected][subIndex] = value;
                    return { form }
                })
            },
            onElementDelete: (index: number, { set }) => {
                set(({ form: prev, rowSelected }) => {
                    if (rowSelected === undefined)
                        return {}
                    const form: IPropsElement[][] = cloneObj(prev);
                    if (form[rowSelected].length === 1) {
                        form.splice(rowSelected, 1);
                    } else {
                        let subIndex = form[rowSelected].findIndex(sub => sub.index === index);
                        if (subIndex > -1) {
                            form[rowSelected].splice(subIndex, 1);
                            for (let i = subIndex; i < form[rowSelected].length; i++) {
                                form[rowSelected][i] = { ...form[rowSelected][i], subIndex };
                                subIndex++;
                            }
                        }
                    }
                    return { form: updateIndex(form), selected: undefined, rowSelected: undefined }
                })
            },
            onElementDuplicate: (value: IPropsElement, { set }) => {
                set(({ form: prev, rowSelected }) => {
                    if (rowSelected === undefined)
                        return {}
                    const form: IPropsElement[][] = cloneObj(prev);
                    const { index } = value;
                    let subIndex = form[rowSelected].findIndex(sub => sub.index === index);
                    if (subIndex > -1) {
                        subIndex++;
                        form[rowSelected].splice(subIndex, 0, { ...value, subIndex });
                        for (let i = subIndex; i < form[rowSelected].length; i++) {
                            subIndex++;
                            form[rowSelected][i] = { ...form[rowSelected][i], subIndex };
                        }
                    }
                    return { form: updateIndex(form) }
                })
            },
            onElementChangePosition: (value: IPropsElement, newPosition: number, { set }) => {
                set(({ form: prev, rowSelected }) => {
                    if (rowSelected === undefined)
                        return {}
                    const form: IPropsElement[][] = cloneObj(prev);
                    const { subIndex = 0 } = value;
                    const newIndex = subIndex + newPosition;
                    form[rowSelected][subIndex] = { ...form[rowSelected][newIndex], subIndex };
                    form[rowSelected][newIndex] = { ...value, subIndex: newIndex };

                    return { form }
                })
            },
            onElementChangeRow: (value: IPropsElement, newRowIndex: number, { set }) => {
                set(({ form: prev, rowSelected }) => {
                    if (rowSelected === undefined)
                        return {}
                    const form: IPropsElement[][] = cloneObj(prev);
                    const { index } = value;
                    form[rowSelected] = form[rowSelected].filter(item => item.index !== index);
                    const newRow = form[newRowIndex] || [];
                    newRow.push({ ...value, subIndex: newRow.length });
                    form[newRowIndex] = newRow;
                    if (form[rowSelected].length === 0) {
                        form.splice(rowSelected, 1);
                    }

                    return { form: updateIndex(form) }
                })
            },
            onRowUpdate: (index: number, targetIndex: number, { set }) => {
                set(({ form: prev, selected, rowSelected }) => {
                    const form: IPropsElement[][] = cloneObj(prev);
                    const temp = form[index];
                    const target = { selected, rowSelected }
                    const start = Math.min(form[index][0].index || 0, form[targetIndex][0].index || 0);

                    if (selected !== undefined && start <= selected) {
                        target.selected = undefined;
                        target.rowSelected = undefined
                    }

                    form[index] = form[targetIndex];
                    form[targetIndex] = temp;

                    return { form: updateIndex(form), ...target }
                })
            },
            onRowDelete: (index: number, { set }) => {
                set(({ form: prev, selected, rowSelected }) => {
                    const form: IPropsElement[][] = cloneObj(prev);
                    const target = { selected, rowSelected }
                    const start = form[index][0].index || 0;

                    if (selected !== undefined && start <= selected) {
                        target.selected = undefined;
                        target.rowSelected = undefined
                    }

                    form.splice(index, 1);
                    return { form: updateIndex(form), ...target }
                })
            }
        }}
    >
        {children}
    </Provider>
}

export { callEventChecklistEdit, ChecklistEditProvider, getChecklistEdit, setChecklistEdit, useChecklistEdit };

